/**
 * assets/theme.js
 * All theme interactivity except the cart AJAX API itself (see cart.js,
 * loaded first). Organized as small, independent modules that each look
 * for their own markup and no-op if it isn't on the page. Loaded with
 * `defer`, so the DOM is already parsed by the time this runs — custom
 * elements defined here upgrade any matching tags immediately.
 *
 * Every string this file ever displays to a shopper is read from a
 * data-attribute that Liquid populated via the `t` filter — nothing here
 * is hardcoded English, so the theme stays fully translatable.
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ==================================================================
     Shared helpers
     ================================================================== */

  function debounce(fn, wait) {
    var timeout;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getFocusableElements(container) {
    return Array.prototype.slice
      .call(container.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      });
  }

  /**
   * Traps Tab/Shift+Tab focus cycling within `container`. Returns a
   * cleanup function that must be called to release the trap.
   */
  function trapFocus(container, elementToFocus) {
    var focusable = getFocusableElements(container);
    if (!focusable.length) return function () {};

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    (elementToFocus || first).focus();

    function handleKeydown(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', handleKeydown);
    return function () {
      container.removeEventListener('keydown', handleKeydown);
    };
  }

  function lockBodyScroll() {
    document.body.style.overflow = 'hidden';
  }

  function unlockBodyScroll() {
    document.body.style.overflow = '';
  }

  /* ==================================================================
     Scroll-reveal (fade + rise) — skipped entirely under reduced motion
     ================================================================== */

  function initScrollReveal() {
    var targets = document.querySelectorAll('[data-animate]');
    if (!targets.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ==================================================================
     Sticky header shadow
     ================================================================== */

  function initStickyHeader() {
    var sentinel = document.querySelector('[data-header-sentinel]');
    var header = document.querySelector('header-component');
    if (!sentinel || !header || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        header.classList.toggle('header--shadow', !entry.isIntersecting);
      });
    });
    observer.observe(sentinel);
  }

  /* ==================================================================
     Header search toggle
     ================================================================== */

  function initHeaderSearch() {
    var toggle = document.querySelector('[data-header-search-toggle]');
    if (!toggle) return;
    var panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;

    toggle.addEventListener('click', function () {
      var isOpen = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isOpen));
      panel.hidden = isOpen;
      if (!isOpen) {
        var input = panel.querySelector('input[type="search"]');
        if (input) input.focus();
      }
    });
  }

  /* ==================================================================
     Mobile nav drawer
     ================================================================== */

  function defineMobileNavDrawer() {
    if (customElements.get('mobile-nav-drawer')) return;

    class MobileNavDrawer extends HTMLElement {
      connectedCallback() {
        this._releaseFocusTrap = null;
        this._toggle = document.querySelector('[data-mobile-nav-toggle]');
        this._panel = this.querySelector('.mobile-nav__panel');
        this._onKeydown = this._onKeydown.bind(this);

        if (this._toggle) {
          this._toggle.addEventListener('click', this.open.bind(this));
        }
        this.querySelectorAll('[data-mobile-nav-close]').forEach(
          function (el) {
            el.addEventListener('click', this.close.bind(this));
          }.bind(this)
        );
      }

      open() {
        this.hidden = false;
        lockBodyScroll();
        if (this._toggle) this._toggle.setAttribute('aria-expanded', 'true');
        document.addEventListener('keydown', this._onKeydown);
        if (this._panel) this._releaseFocusTrap = trapFocus(this._panel);
      }

      close() {
        this.hidden = true;
        unlockBodyScroll();
        if (this._toggle) {
          this._toggle.setAttribute('aria-expanded', 'false');
          this._toggle.focus();
        }
        document.removeEventListener('keydown', this._onKeydown);
        if (this._releaseFocusTrap) this._releaseFocusTrap();
      }

      _onKeydown(event) {
        if (event.key === 'Escape') this.close();
      }
    }

    customElements.define('mobile-nav-drawer', MobileNavDrawer);
  }

  /* ==================================================================
     Cart drawer
     ================================================================== */

  function formatMoney(cents) {
    return window.CartApi ? window.CartApi.formatMoney(cents) : cents;
  }

  function buildCartItemRow(item) {
    var template = document.getElementById('CartDrawerItemTemplate');
    if (!template) return null;
    var node = template.content.firstElementChild.cloneNode(true);

    node.setAttribute('data-line', item.line || '');

    var imageLink = node.querySelector('[data-slot="image-link"]');
    var image = node.querySelector('[data-slot="image"]');
    if (item.image) {
      image.src = item.image;
      image.alt = item.title;
    }
    if (imageLink) imageLink.href = item.url;

    var title = node.querySelector('[data-slot="title"]');
    title.textContent = item.product_title || item.title;
    title.href = item.url;

    var variant = node.querySelector('[data-slot="variant"]');
    if (item.variant_title && item.variant_title !== 'Default Title') {
      variant.textContent = item.variant_title;
      variant.hidden = false;
    }

    var price = node.querySelector('[data-slot="price"]');
    if (price) price.textContent = formatMoney(item.final_price != null ? item.final_price : item.price);

    var quantityInput = node.querySelector('[data-quantity-field]');
    if (quantityInput) quantityInput.value = item.quantity;
    var quantityEl = node.querySelector('[data-quantity-input]');
    if (quantityEl) quantityEl.setAttribute('data-line', item.line || '');

    var removeBtn = node.querySelector('[data-slot="remove"]');
    if (removeBtn) removeBtn.setAttribute('data-line', item.line || '');

    return node;
  }

  function renderCartDrawer(cart) {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    var list = drawer.querySelector('[data-cart-items-list]');
    var empty = drawer.querySelector('[data-cart-empty-state]');
    var footer = drawer.querySelector('[data-cart-drawer-footer]');
    var countEl = drawer.querySelector('[data-cart-drawer-count]');
    var subtotalEl = drawer.querySelector('[data-cart-subtotal]');

    if (countEl) countEl.textContent = '(' + cart.item_count + ')';
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    if (cart.item_count === 0) {
      if (list) list.hidden = true;
      if (empty) empty.hidden = false;
      if (footer) footer.hidden = true;
      return;
    }

    if (empty) empty.hidden = true;
    if (footer) footer.hidden = false;
    if (list) {
      list.hidden = false;
      list.innerHTML = '';
      cart.items.forEach(function (item, index) {
        item.line = index + 1;
        var row = buildCartItemRow(item);
        if (row) list.appendChild(row);
      });
    }

    var shippingProgress = drawer.querySelector('[data-shipping-progress]');
    if (shippingProgress) {
      var threshold = parseInt(shippingProgress.getAttribute('data-threshold'), 10);
      var fill = shippingProgress.querySelector('[data-shipping-fill]');
      var message = shippingProgress.querySelector('[data-shipping-message]');
      var remaining = threshold - cart.total_price;
      var ratio = Math.min(cart.total_price / threshold, 1);
      if (fill) fill.style.width = ratio * 100 + '%';
      if (message) {
        if (remaining > 0) {
          message.textContent = message.getAttribute('data-remaining-template')
            ? message.getAttribute('data-remaining-template').replace('{{ amount }}', formatMoney(remaining))
            : message.textContent;
        } else if (message.getAttribute('data-achieved-text')) {
          message.textContent = message.getAttribute('data-achieved-text');
        }
      }
    }
  }

  function announceCartLiveRegion(key) {
    var region = document.querySelector('[data-cart-live-region]');
    if (!region) return;
    var text = region.getAttribute('data-' + key + '-text');
    if (text) region.textContent = text;
  }

  function updateHeaderCartSection(html) {
    if (!html) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var freshBubble = parsed.querySelector('#cart-icon-bubble');
    var liveBubble = document.getElementById('cart-icon-bubble');
    if (freshBubble && liveBubble) {
      liveBubble.innerHTML = freshBubble.innerHTML;
      liveBubble.hidden = freshBubble.hidden;
    }
  }

  /**
   * Keeps the header's cart-count bubble in sync with every cart mutation,
   * independent of the cart drawer's own lifecycle — settings.cart_type
   * can be "page", in which case <cart-drawer> never exists in the DOM at
   * all, but the header cart icon still needs its count kept current.
   */
  function initCartCountBadge() {
    document.addEventListener('cart:updated', function (event) {
      var response = event.detail && event.detail.response;
      if (response && response.sections && response.sections.header) {
        updateHeaderCartSection(response.sections.header);
      }
    });
  }

  function defineCartDrawer() {
    if (customElements.get('cart-drawer')) return;

    class CartDrawer extends HTMLElement {
      connectedCallback() {
        this._releaseFocusTrap = null;
        this._onKeydown = this._onKeydown.bind(this);
        this._cartLink = document.querySelector('[data-cart-icon-link]');

        this.querySelectorAll('[data-cart-drawer-close]').forEach(
          function (el) {
            el.addEventListener('click', this.close.bind(this));
          }.bind(this)
        );

        if (this._cartLink) {
          this._cartLink.addEventListener(
            'click',
            function (event) {
              event.preventDefault();
              this.open();
            }.bind(this)
          );
        }

        this.querySelectorAll('[data-cart-remove]').forEach(
          function (btn) {
            btn.addEventListener('click', this._handleRemove.bind(this));
          }.bind(this)
        );

        var note = this.querySelector('[data-cart-note]');
        if (note) {
          note.addEventListener(
            'input',
            debounce(function () {
              window.CartApi.updateNote(note.value);
            }, 500)
          );
        }

        document.addEventListener('cart:updated', this._handleCartUpdated.bind(this));
      }

      open() {
        this.hidden = false;
        lockBodyScroll();
        var panel = this.querySelector('.cart-drawer__panel');
        if (panel) this._releaseFocusTrap = trapFocus(panel, this.querySelector('[data-cart-drawer-close]'));
        document.addEventListener('keydown', this._onKeydown);
      }

      close() {
        this.hidden = true;
        unlockBodyScroll();
        document.removeEventListener('keydown', this._onKeydown);
        if (this._releaseFocusTrap) this._releaseFocusTrap();
        if (this._cartLink) this._cartLink.focus();
      }

      _onKeydown(event) {
        if (event.key === 'Escape') this.close();
      }

      _handleRemove(event) {
        var line = event.currentTarget.closest('[data-cart-item]').getAttribute('data-line');
        window.CartApi.changeItem(parseInt(line, 10), 0);
      }

      _handleCartUpdated(event) {
        // Header cart-count updates are handled by a page-level listener
        // (see initCartCountBadge) that doesn't depend on this element
        // existing, since settings.cart_type can be "page" with no drawer
        // in the DOM at all. This handler only owns the drawer's own markup.
        var detail = event.detail || {};
        var cart = detail.response;
        if (!cart) return;

        renderCartDrawer(cart);

        if (detail.type === 'add') {
          announceCartLiveRegion('added');
          this.open();
        }
      }
    }

    customElements.define('cart-drawer', CartDrawer);
  }

  /* ==================================================================
     Quantity input (+/- stepper), wired to cart AJAX when in a cart context
     ================================================================== */

  function defineQuantityInput() {
    if (customElements.get('quantity-input')) return;

    class QuantityInput extends HTMLElement {
      connectedCallback() {
        this._field = this.querySelector('[data-quantity-field]');
        this._context = this.getAttribute('data-context') || 'product';

        var decrease = this.querySelector('[data-quantity-decrease]');
        var increase = this.querySelector('[data-quantity-increase]');
        if (decrease) decrease.addEventListener('click', this._step.bind(this, -1));
        if (increase) increase.addEventListener('click', this._step.bind(this, 1));

        if (this._context === 'cart-drawer' || this._context === 'cart-page') {
          this._field.addEventListener('change', debounce(this._commit.bind(this), 300));
        }
      }

      _step(direction) {
        var min = parseInt(this._field.min, 10) || 0;
        var max = this._field.max ? parseInt(this._field.max, 10) : Infinity;
        var value = (parseInt(this._field.value, 10) || 0) + direction;
        value = Math.max(min, Math.min(max, value));
        this._field.value = value;
        this._field.dispatchEvent(new Event('change', { bubbles: true }));
      }

      _commit() {
        var line = this.getAttribute('data-line');
        if (!line || !window.CartApi) return;
        var quantity = parseInt(this._field.value, 10) || 0;
        window.CartApi.changeItem(parseInt(line, 10), quantity);
      }
    }

    customElements.define('quantity-input', QuantityInput);
  }

  /* ==================================================================
     Announcement bar rotation
     ================================================================== */

  function defineAnnouncementBar() {
    if (customElements.get('announcement-bar')) return;

    class AnnouncementBar extends HTMLElement {
      connectedCallback() {
        this._items = Array.prototype.slice.call(this.querySelectorAll('[data-announcement-item]'));
        if (this._items.length > 1) {
          this.classList.add('is-enhanced');
          this._items.forEach(function (item, index) {
            item.hidden = false;
            item.classList.toggle('is-active', index === 0);
          });
          this._index = 0;

          if (this.getAttribute('data-auto-rotate') === 'true' && !prefersReducedMotion) {
            this._interval = parseInt(this.getAttribute('data-interval'), 10) * 1000 || 5000;
            this._start();
            this.addEventListener('mouseenter', this._stop.bind(this));
            this.addEventListener('mouseleave', this._start.bind(this));
            this.addEventListener('focusin', this._stop.bind(this));
            this.addEventListener('focusout', this._start.bind(this));
          }
        }

        var dismiss = this.querySelector('[data-announcement-dismiss]');
        if (dismiss) {
          var storageKey = 'announcement-dismissed-' + this.getAttribute('data-section-id');
          if (window.sessionStorage && sessionStorage.getItem(storageKey)) {
            this.hidden = true;
          }
          dismiss.addEventListener(
            'click',
            function () {
              this.hidden = true;
              this._stop();
              if (window.sessionStorage) sessionStorage.setItem(storageKey, 'true');
            }.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this._stop();
      }

      _start() {
        if (this._timer || !this._interval) return;
        this._timer = setInterval(this._advance.bind(this), this._interval);
      }

      _stop() {
        clearInterval(this._timer);
        this._timer = null;
      }

      _advance() {
        this._items[this._index].classList.remove('is-active');
        this._index = (this._index + 1) % this._items.length;
        this._items[this._index].classList.add('is-active');
      }
    }

    customElements.define('announcement-bar', AnnouncementBar);
  }

  /* ==================================================================
     Generic accordion (FAQ + product Description/Details/Shipping rows)
     ================================================================== */

  function initAccordions() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-accordion-trigger]');
      if (!trigger) return;

      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;

      var isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!isExpanded));
      panel.hidden = isExpanded;
    });
  }

  /* ==================================================================
     Sample chapter "read more" expander
     ================================================================== */

  function initExpandables() {
    document.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-expandable-toggle]');
      if (!toggle) return;

      var content = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!content) return;

      var isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isExpanded));
      content.classList.toggle('is-expanded', !isExpanded);

      var expandLabel = toggle.querySelector('[data-expand-label]');
      var collapseLabel = toggle.querySelector('[data-collapse-label]');
      if (expandLabel) expandLabel.hidden = !isExpanded;
      if (collapseLabel) collapseLabel.hidden = isExpanded;
    });
  }

  /* ==================================================================
     Buy buttons: variant picker + add-to-cart submit
     ================================================================== */

  function getSelectedOptionValues(root) {
    var byPosition = {};
    root.querySelectorAll('[data-variant-option]:checked').forEach(function (radio) {
      var fieldset = radio.closest('[data-variant-picker]');
      var position = parseInt(fieldset.getAttribute('data-option-position'), 10);
      byPosition[position] = radio.value;
    });
    var positions = Object.keys(byPosition)
      .map(Number)
      .sort(function (a, b) {
        return a - b;
      });
    return positions.map(function (position) {
      return byPosition[position];
    });
  }

  function findMatchingVariant(variants, selectedValues) {
    return variants.filter(function (variant) {
      return selectedValues.every(function (value, index) {
        return variant['option' + (index + 1)] === value;
      });
    })[0];
  }

  function updatePriceDisplay(container, variant) {
    if (!container) return;
    var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    if (onSale) {
      container.innerHTML =
        '<s class="price__compare-at">' +
        formatMoney(variant.compare_at_price) +
        '</s><span class="price__sale">' +
        formatMoney(variant.price) +
        '</span>';
    } else {
      container.innerHTML = '<span class="price__regular">' + formatMoney(variant.price) + '</span>';
    }
  }

  function initBuyButtons(root) {
    var form = root.querySelector('form');
    if (!form) return;

    var variantsScript = root.querySelector('[data-product-variants-json]');
    var hiddenInput = form.querySelector('[data-buy-buttons-variant-id]');
    var addButton = form.querySelector('[data-add-to-cart]');
    var checkoutWrapper = root.querySelector('[data-dynamic-checkout-wrapper]');
    var errorEl = form.querySelector('[data-buy-buttons-error]');
    var priceContainer = root.parentElement ? root.parentElement.querySelector('.price') : null;

    var variants = [];
    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        variants = [];
      }
    }

    if (variants.length) {
      root.querySelectorAll('[data-variant-option]').forEach(function (radio) {
        radio.addEventListener('change', function () {
          var selected = getSelectedOptionValues(root);
          var variant = findMatchingVariant(variants, selected);
          if (!variant) return;

          hiddenInput.value = variant.id;
          hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
          updatePriceDisplay(priceContainer, variant);

          if (addButton) {
            addButton.disabled = !variant.available;
            var label = addButton.querySelector('.button__label');
            if (label) {
              label.textContent = variant.available
                ? addButton.getAttribute('data-add-to-cart-text')
                : addButton.getAttribute('data-sold-out-text');
            }
          }
          if (checkoutWrapper) checkoutWrapper.hidden = !variant.available;
        });
      });
    }

    form.addEventListener('submit', function (event) {
      if (!window.CartApi) return; // graceful no-JS-API fallback: native form submit proceeds
      event.preventDefault();
      if (errorEl) errorEl.hidden = true;
      if (addButton) addButton.setAttribute('aria-busy', 'true');

      var quantityField = form.querySelector('[data-quantity-field]');
      var quantity = quantityField ? parseInt(quantityField.value, 10) || 1 : 1;

      window.CartApi.addItem(hiddenInput.value, quantity)
        .catch(function () {
          if (errorEl) errorEl.hidden = false;
          announceCartLiveRegion('error');
        })
        .then(function () {
          if (addButton) addButton.removeAttribute('aria-busy');
        });
    });
  }

  function initAllBuyButtons() {
    document.querySelectorAll('[data-buy-buttons]').forEach(initBuyButtons);
  }

  /* ==================================================================
     Sticky mobile add-to-cart bar
     ================================================================== */

  function initStickyAddToCart() {
    var bar = document.querySelector('[data-sticky-atc]');
    var addButton = document.querySelector('[data-buy-buttons] [data-add-to-cart]');
    if (!bar || !addButton || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.hidden = entry.isIntersecting;
      });
    });
    observer.observe(addButton);

    var stickyButton = bar.querySelector('[data-sticky-atc-button]');
    if (stickyButton) {
      stickyButton.addEventListener('click', function () {
        addButton.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'center' });
        addButton.focus({ preventScroll: true });
      });
    }
  }

  /* ==================================================================
     Product gallery thumbnails
     ================================================================== */

  function initProductGallery() {
    var gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;

    gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var mediaId = thumb.getAttribute('data-media-id');

        gallery.querySelectorAll('[data-gallery-slide]').forEach(function (slide) {
          var isMatch = slide.getAttribute('data-media-id') === mediaId;
          slide.hidden = !isMatch;
          slide.classList.toggle('is-active', isMatch);
        });

        gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (t) {
          var isMatch = t === thumb;
          t.classList.toggle('is-active', isMatch);
          t.setAttribute('aria-selected', String(isMatch));
        });
      });
    });
  }

  /* ==================================================================
     Author video click-to-load
     ================================================================== */

  function initAuthorVideo() {
    var frame = document.querySelector('[data-video-embed]');
    if (!frame) return;

    var playButton = frame.querySelector('[data-video-play]');
    if (!playButton) return;

    playButton.addEventListener('click', function () {
      var provider = frame.getAttribute('data-provider');
      var videoId = frame.getAttribute('data-video-id');
      var title = frame.getAttribute('data-video-title') || '';
      var src =
        provider === 'vimeo'
          ? 'https://player.vimeo.com/video/' + videoId + '?autoplay=1'
          : 'https://www.youtube-nocookie.com/embed/' + videoId + '?autoplay=1&rel=0';

      var iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.title = title;
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      iframe.setAttribute('allowfullscreen', '');

      frame.innerHTML = '';
      frame.appendChild(iframe);
    });
  }

  /* ==================================================================
     Init
     ================================================================== */

  defineMobileNavDrawer();
  defineCartDrawer();
  defineQuantityInput();
  defineAnnouncementBar();

  initScrollReveal();
  initStickyHeader();
  initHeaderSearch();
  initCartCountBadge();
  initAccordions();
  initExpandables();
  initAllBuyButtons();
  initStickyAddToCart();
  initProductGallery();
  initAuthorVideo();

  document.addEventListener('cart:error', function () {
    announceCartLiveRegion('error');
  });
})();
